import {
  Checkbox,
  Grid,
  Select,
  Text,
  useMantineColorScheme,
} from "@mantine/core";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { useCurrency } from "../../../../utils/helpers";
import PropTypes from "prop-types";
import { responsiveCol, useAssessmentRouteParams } from "../../../../constants/helper";
import { useAtom } from "jotai";
import { projectDetailsAtom } from "../../../../utils/atoms/projectDetailsAtom";
import { useMemo, useState, useEffect, useRef } from "react";
import TimeSeriesGraph from "../../../timeSeriesGraph";
import MGAAPI from "../../../../api/index";
import displayError from "../../../../constants/utils";
import { getErrorMessage } from "../../../../utils/errorUtils";
import ProjectNumberInput from "../../../common/ProjectNumberInput";
import { useTranslation } from "react-i18next";

const MGA_API = new MGAAPI();

const Co2EmissionFixed = (props) => {
  const { form, handleInputChange, gridParameters, getFieldAttributes, isNewGrid } = props;
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const currency = useCurrency();
  const [projectDetails] = useAtom(projectDetailsAtom);
  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const [variableOptionData, setVariableOptionData] = useState([]); 
  const [isCo2EmissionLoading, setIsCo2EmissionLoading] = useState(false);
  const hasFetchedInitialFixedProfile = useRef(false);

  useEffect(() => {
    const profileData = gridParameters?.componentAttributes?.gridParameters?.profileFile || [];
    setVariableOptionData(profileData);
  }, [gridParameters?.componentAttributes?.gridParameters?.profileFile]);

  const co2Enabled = Boolean(
    gridParameters?.uiAttributes?.gridParameters?.co2Optimization
  );
  

 const grayIconStyle = useMemo(
   () => ({
     padding: "4px",
   }),
   [colorScheme],
  ); 

  // Determine if any relevant TextInput has validation errors
  const hasTextInputErrors = Boolean(
    form?.errors?.[
      "componentAttributes.gridParameters.co2Emission.gridEmissionFactor"
    ] || form?.errors?.["economicAttributes.gridParameters.price"]
  );

 const fetchCo2EmissionData = (co2EmissionType) => {
   setIsCo2EmissionLoading(true);
  MGA_API.getCo2EmissionProfile(projectId, designId, designVersionId, co2EmissionType)
     .then((response) => {
       const { co2Profile, gridEmissionFactor } = response || {};

       if (co2EmissionType === "Variable") {
         setVariableOptionData(co2Profile || []);
         handleInputChange("componentAttributes", "profileFile", co2Profile || []);
       } else {
         setVariableOptionData([]);
         handleInputChange("componentAttributes", "profileFile", []);
         handleInputChange(
           "componentAttributes",
           "co2Emission.gridEmissionFactor",
           gridEmissionFactor ?? ""
         );
       }
       setIsCo2EmissionLoading(false);
     })
     .catch((error) => {
       setVariableOptionData([]);
       handleInputChange("componentAttributes", "profileFile", []);
       setIsCo2EmissionLoading(false);
       displayError("error", getErrorMessage(error));
     });
 };

  useEffect(() => {
    const co2EmissionType =
      gridParameters?.componentAttributes?.gridParameters?.co2Emission?.type;
    if (!isNewGrid || co2EmissionType !== "Fixed" || hasFetchedInitialFixedProfile.current) {
      return;
    }

    hasFetchedInitialFixedProfile.current = true;
    fetchCo2EmissionData("Fixed");
  }, [
    gridParameters?.componentAttributes?.gridParameters?.co2Emission?.type,
    isNewGrid,
  ]);

  return (
    <>
      <Text component="span" fw={700}>
        {t("grid.co2Emission.header")}
      </Text>
      {/* <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col
          className={`d-flex ${
            hasTextInputErrors ? "align-items-center" : "align-items-end"
          }`}
          {...responsiveCol}
        >
          <Checkbox
            label={
              <>
                Enable CO<sub>2</sub> Optimization
              </>
            }
            name="co2_emission.co2_optimization"
            checked={
              gridParameters?.uiAttributes?.gridParameters?.co2Optimization
            }
            {...form.getInputProps(
              "uiAttributes.gridParameters.co2Optimization"
            )}
            onChange={(event) => {
              handleInputChange(
                "uiAttributes",
                "co2Optimization",
                event.currentTarget.checked
              );
            }}
          />
        </Grid.Col>
      </Grid> */}
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <Select
            {...getFieldAttributes("field-1")}
            data-id="field-1"
            data-testid="co2-emission-type"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("grid.co2Emission.type.label")}
            data={[
              { value: "Fixed", label: t("grid.co2Emission.type.fixed") },
              { value: "Variable", label: t("grid.co2Emission.type.variable") },
            ]}
            withAsterisk={co2Enabled}
            required={co2Enabled}
            placeholder={t("common.selectOption")}
            value={
              gridParameters?.componentAttributes?.gridParameters?.co2Emission
                ?.type
            }
            {...form.getInputProps(
              "componentAttributes.gridParameters.co2Emission.type"
            )}
            onChange={(value) => {
              handleInputChange(
                "componentAttributes",
                "co2Emission.type",
                value
              );
              fetchCo2EmissionData(value);
            }}
            withinPortal
          />
        </Grid.Col>
        {gridParameters?.componentAttributes?.gridParameters?.co2Emission
          ?.type === "Fixed" && (
          <Grid.Col {...responsiveCol}>
            <ProjectNumberInput
              {...getFieldAttributes("field-3")}
              data-id="field-3"
              label={t("grid.co2Emission.fixedRate.label")}
              placeholder={t("grid.co2Emission.fixedRate.placeholder")}
              {...form.getInputProps(
                "componentAttributes.gridParameters.co2Emission.gridEmissionFactor"
              )}
              onChange={(event) => {
                handleInputChange(
                  "componentAttributes",
                  "co2Emission.gridEmissionFactor",
                  event.target.value
                );
              }}
              required={co2Enabled}
              data-testid="co2-grid-emission"
              rightSection={
                <div style={{ ...grayIconStyle, marginRight: "1.5rem" }}>
                  g/kWh
                </div>
              }
              rightSectionProps={{ style: { pointerEvents: "none" } }}
              rightSectionWidth={60}
            />
          </Grid.Col>
        )}
        {/* <Grid.Col {...responsiveCol}>
          <TextInput
            {...getFieldAttributes("field-4")}
            data-id="field-4"
            data-testid="co2-price"
            label={
              <>
                CO<sub>2</sub> {`Emission Cost Impact`}
              </>
            }
            id="price"
            type="number"
            name="co2_emission.price"
            placeholder="Price of Co2 Emission"
            maxLength="100"
            required={co2Enabled}
            {...form.getInputProps("economicAttributes.gridParameters.price")}
            onChange={(event) => {
              handleInputChange(
                "economicAttributes",
                "price",
                event.target.value
              );
            }}
            rightSection={
              <div style={{ ...grayIconStyle, marginRight: "1rem" }}>
                {currency || "$"}/Tonne
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            rightSectionWidth={60}
          />
        </Grid.Col> */}
      </Grid>
      {gridParameters?.componentAttributes?.gridParameters?.co2Emission
          ?.type === "Variable" &&
        <Grid gutter="md" className="mb-2">
         <Grid.Col
           gutter="md"
           className="mt-2 mb-2"
           id="timeSeries"
           style={{
             position: "relative",
             maxWidth: "100% !important",
             boxSizing: "border-box",
           }}
         >
           {isCo2EmissionLoading && <se-loading loading={isCo2EmissionLoading} />}
           {variableOptionData?.length > 0 && (
             <TimeSeriesGraph
               dataAssset={variableOptionData}
               color={"#36a2eb"}
               name={t("grid.co2Emission.rate")}
               yAxisName="g/kWh"
               embedHeight={500}
               showMinimap={true}
               minimapGap={40}
               legendLabels={{ electricalLoad: t("grid.co2Emission.rate") }}
               tooltipLabels={{ generic: t("grid.co2Emission.rate") }}
               isUnitsUnique={true}
               showEnergy={false}
             />
           )}
          </Grid.Col>
      </Grid>}
    </>
  );
};

Co2EmissionFixed.propTypes = {
  form: PropTypes.object,
  handleInputChange: PropTypes.func,
  gridParameters: PropTypes.object,
  getFieldAttributes: PropTypes.func,
  isNewGrid: PropTypes.bool,
};

export default Co2EmissionFixed;
